import { createFileRoute } from '@tanstack/react-router'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { useLingui } from '@lingui/react/macro'
import { UserIcon, BuildingIcon, BriefcaseIcon, EyeIcon, MailIcon, CalendarIcon } from 'lucide-react'
import { trpc } from '@/main'
import { formatDateLocale } from '@/lib/utils'
import { useTranslation } from '@/lib/locales'
import { useRootSearch } from '@/hooks/use-root-search'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { SearchHeader } from './-components/api/search-header'
import { CompletionIndicator } from './-components/shared/completion-indicator'
import { Pagination } from './-components/shared/pagination'
import { SimpleListPending } from './-components/app-layout-skeleton'

export const Route = createFileRoute('/_app/account/api/$appId/applicants')({
  loaderDeps: ({ search }) => ({ p: search.p, q: search.q }),
  // The list's key, `q` and page included. The search box writes `q` to the URL (useDebouncedSearch,
  // as every other list in the app does), so the loader and the list read the ONE object built here —
  // there is no second, searched key for a component to mint, and no factory to hold the two in sync.
  context: ({ deps, params }) => ({
    appApplicantsQueryOptions: trpc.apps.getApplicants.queryOptions({
      appClientId: params.appId,
      search: { q: deps.q || undefined, p: deps.p },
    }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.appApplicantsQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: SimpleListPending,
})

function RouteComponent() {
  const { language } = useTranslation()
  const { t } = useLingui()
  const { q } = useRootSearch()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: q })

  const context = Route.useRouteContext()
  // Defer the OPTIONS object (the context is reference-stable per match), so a new search keeps the
  // previous page on screen while the next one loads instead of flashing the skeleton.
  const deferredOptions = useDeferredValue(context.appApplicantsQueryOptions)
  const { data: applicants, isFetching } = useSuspenseQuery(deferredOptions)
  // True while the deferred options lag the live ones — keeps the dimming through that transition.
  const isStale = isFetching || deferredOptions !== context.appApplicantsQueryOptions

  return (
    <div className="flex flex-col gap-4">
      <SearchHeader query={searchInput} onQueryChange={handleSearchChange} count={applicants.pagination.count} />

      <div className="flex flex-col gap-3" style={{ opacity: isStale ? 0.6 : undefined }}>
        {applicants.list.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <UserIcon className="text-muted-foreground/50 size-12" />
            <p className="text-muted-foreground">{t`No applicants found`}</p>
          </div>
        )}

        {applicants.list.map((elem) => {
          const { jobs, jobsAddedMe } = elem
          return (
            <div key={elem.id} className="bg-card rounded-lg border p-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{elem.name ?? t`Unnamed`}</h3>
                    <code className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-xs">{elem.id}</code>
                    <CompletionIndicator
                      skills={elem.testPassed}
                      values={elem.testPassedValues}
                      traits={elem.testPassedPersonality}
                      size="md"
                    />
                  </div>

                  <div className="text-muted-foreground flex flex-col gap-1 text-xs sm:items-end">
                    {elem.created && (
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="size-3" />
                        {t`Created`}: {formatDateLocale(elem.created, language)}
                      </span>
                    )}
                    {elem.accessed && (
                      <span className="flex items-center gap-1">
                        <EyeIcon className="size-3" />
                        {t`Accessed`}: {formatDateLocale(elem.accessed, language)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
                  {elem.email && (
                    <span className="flex items-center gap-1">
                      <MailIcon className="size-3.5" />
                      {elem.email}
                    </span>
                  )}
                  {elem.company && (
                    <span className="flex items-center gap-1">
                      <BuildingIcon className="size-3.5" />
                      {elem.company}
                    </span>
                  )}
                </div>

                <div className="text-muted-foreground flex items-center gap-4 border-t pt-2 text-sm">
                  <span className="flex items-center gap-1">
                    <BriefcaseIcon className="size-3.5" />
                    {jobs === 1 ? t`${jobs} job` : t`${jobs} jobs`}
                  </span>
                  <span className="flex items-center gap-1">{t`${jobsAddedMe} added`}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <Pagination pagination={applicants.pagination} />
    </div>
  )
}
